import React from "react";
import AlternateEmailRoundedIcon from "@mui/icons-material/AlternateEmailRounded";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import LocationCityOutlinedIcon from "@mui/icons-material/LocationCityOutlined";
import PhotoCameraRoundedIcon from "@mui/icons-material/PhotoCameraRounded";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";

import PageHeader from "../../../Components/PageHeader/PageHeader";
import UserAvatarIcon from "../../../Components/UserAvatarIcon/UserAvatarIcon";
import { isValidEmail } from "../../../utils/validation";
import {
  PROFILE_PHOTO_ACCEPTED_TYPES,
  PROFILE_PHOTO_MAX_BYTES,
} from "../../../services/portalAluno/profile/profileService";
import useProfile from "../../../hooks/PortalAluno/useProfile";
import useProfilePhoto from "../../../hooks/PortalAluno/useProfilePhoto";
import useRemoveProfilePhoto from "../../../hooks/PortalAluno/useRemoveProfilePhoto";
import MenuPageBackLink from "../MenuShared/MenuPageBackLink";
import "./styles.css";

const ProfilePage = () => {
  const { auth, error, isLoading, mutateAsync, reset } = useProfile();
  const authEmail = auth?.emailUsuario ?? "";
  const [isEditingEmail, setIsEditingEmail] = React.useState(false);
  const [email, setEmail] = React.useState(authEmail);
  const [validationMessage, setValidationMessage] = React.useState("");
  const [successMessage, setSuccessMessage] = React.useState("");
  const [photoStatus, setPhotoStatus] = React.useState<{
    detail: string;
    isError: boolean;
    label: string;
  } | null>(null);
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const photoInputRef = React.useRef<HTMLInputElement>(null);
  const photoUpload = useProfilePhoto();
  const photoRemoval = useRemoveProfilePhoto();
  const photoUrl = auth?.imgUsuario;
  const isPhotoBusy = photoUpload.isLoading || photoRemoval.isLoading;

  const handlePhotoChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];

    event.target.value = "";

    if (!file) return;

    if (!PROFILE_PHOTO_ACCEPTED_TYPES.includes(file.type)) {
      photoUpload.reset();
      setPhotoStatus({
        detail: "Envie uma imagem JPG, PNG ou WEBP.",
        isError: true,
        label: "Formato inválido",
      });
      return;
    }

    if (file.size > PROFILE_PHOTO_MAX_BYTES) {
      photoUpload.reset();
      setPhotoStatus({
        detail: "A imagem deve ter no máximo 5 MB.",
        isError: true,
        label: "Máx. 5 MB",
      });
      return;
    }

    setPhotoStatus(null);

    try {
      await photoUpload.mutateAsync(file);
      setPhotoStatus({
        detail: "Foto de perfil atualizada.",
        isError: false,
        label: "Atualizado",
      });
    } catch (uploadError) {
      setPhotoStatus({
        detail:
          uploadError instanceof Error &&
          uploadError.message !== "Network Error"
            ? uploadError.message
            : "Não foi possível enviar a foto. Verifique sua conexão e tente novamente.",
        isError: true,
        label: "Falha no envio",
      });
    }
  };

  const handlePhotoRemoval = async () => {
    setPhotoStatus(null);

    try {
      await photoRemoval.mutateAsync();
      setPhotoStatus({
        detail: "Foto de perfil removida.",
        isError: false,
        label: "Foto removida",
      });
    } catch (removalError) {
      setPhotoStatus({
        detail:
          removalError instanceof Error &&
          removalError.message !== "Network Error"
            ? removalError.message
            : "Não foi possível remover a foto. Verifique sua conexão e tente novamente.",
        isError: true,
        label: "Falha ao remover",
      });
    }
  };

  React.useEffect(() => {
    setEmail(authEmail);
  }, [authEmail]);

  React.useEffect(() => {
    if (!isEditingEmail) return;
    const input = dialogRef.current?.querySelector("input");
    input?.focus();
  }, [isEditingEmail]);

  const closeEmailEditor = () => {
    if (isLoading) return;
    setEmail(authEmail);
    setValidationMessage("");
    reset();
    setIsEditingEmail(false);
  };

  const handleEmailSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const normalizedEmail = email.trim();

    if (!isValidEmail(normalizedEmail)) {
      setValidationMessage("Informe um e-mail válido.");
      return;
    }

    setValidationMessage("");
    setSuccessMessage("");
    try {
      await mutateAsync(normalizedEmail);
      setSuccessMessage("E-mail atualizado com sucesso.");
      setIsEditingEmail(false);
    } catch {
      setValidationMessage(
        "Não foi possível atualizar o e-mail. Verifique sua conexão e tente novamente."
      );
    }
  };

  const fields = [
    {
      icon: <BadgeOutlinedIcon />,
      label: "Nome",
      span: 4,
      value: auth?.nmUsuario,
    },
    {
      icon: <AlternateEmailRoundedIcon />,
      label: "E-mail",
      span: 5,
      value: auth?.emailUsuario,
    },
    {
      icon: <BusinessCenterOutlinedIcon />,
      label: "Perfil",
      span: 6,
      value: auth?.nmPerfil,
    },
    {
      icon: <LocationCityOutlinedIcon />,
      label: "Cidade",
      span: 6,
      value: auth?.nmMunicipio,
    },
  ];

  return (
    <section className="content-page profile-page" aria-label="Meus Dados">
      <MenuPageBackLink />

      <PageHeader
        title="Meus Dados"
        description="Confira as informações vinculadas ao seu acesso no Conectado."
      />

      {successMessage && (
        <p className="profile-page__success" role="status">
          {successMessage}
        </p>
      )}

      <div className="profile-page__card">
        <div className="profile-page__fields">
          <div className="profile-field profile-photo-card">
            <div className="profile-photo">
              <span className="profile-photo__frame">
                {photoUrl ? (
                  <img src={photoUrl} alt="Foto de perfil" />
                ) : (
                  <UserAvatarIcon className="profile-photo__placeholder" />
                )}
                {isPhotoBusy && (
                  <span
                    className="profile-photo__loading"
                    role="status"
                    aria-label={
                      photoRemoval.isLoading
                        ? "Removendo foto de perfil"
                        : "Enviando foto de perfil"
                    }
                  >
                    <span
                      className="profile-photo__spinner"
                      aria-hidden="true"
                    />
                  </span>
                )}
              </span>
            </div>

            <div>
              <span className="profile-photo__label-row">
                <span className="profile-field__label">Foto de perfil</span>
                {photoStatus && (
                  <span
                    className={`profile-photo__message${
                      photoStatus.isError
                        ? " profile-photo__message--error"
                        : ""
                    }`}
                    role={photoStatus.isError ? "alert" : "status"}
                    title={photoStatus.detail}
                  >
                    {photoStatus.label}
                  </span>
                )}
              </span>
              <span className="profile-photo__actions">
                <button
                  type="button"
                  className="profile-photo__button"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={isPhotoBusy}
                >
                  <PhotoCameraRoundedIcon aria-hidden="true" />
                  <span>
                    {photoUpload.isLoading
                      ? "Enviando…"
                      : "Enviar foto"}
                  </span>
                </button>

                {photoUrl && (
                  <button
                    type="button"
                    className="profile-photo__remove"
                    onClick={handlePhotoRemoval}
                    disabled={isPhotoBusy}
                  >
                    {photoRemoval.isLoading ? "Removendo…" : "Remover foto"}
                  </button>
                )}
              </span>
            </div>

            <input
              ref={photoInputRef}
              type="file"
              className="profile-photo__input"
              accept={PROFILE_PHOTO_ACCEPTED_TYPES.join(",")}
              onChange={handlePhotoChange}
              aria-label="Selecionar foto de perfil"
            />
          </div>

          {fields.map((field) => (
            <div
              key={field.label}
              className={`profile-field profile-field--span-${field.span}`}
            >
              <span className="profile-field__icon" aria-hidden="true">
                {field.icon}
              </span>
              <div>
                <span className="profile-field__label">{field.label}</span>
                <strong>{field.value?.trim() || "Não informado"}</strong>
              </div>
            </div>
          ))}
        </div>

        <div className="profile-page__help">
          <ReportProblemOutlinedIcon aria-hidden="true" />
          <div>
            <h2>Encontrou algum dado divergente?</h2>
            <p>Envie uma solicitação para que o time responsável possa analisar.</p>
          </div>
          <a
            href="https://iga-claro.identitynow.com/ui/d/mysailpoint"
            target="_blank"
            rel="noopener noreferrer"
          >
            Atualizar Cadastro
          </a>
        </div>
      </div>

      {isEditingEmail && (
        <div
          className="profile-dialog-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeEmailEditor();
          }}
        >
          <div
            ref={dialogRef}
            className="profile-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="profile-dialog-title"
            onKeyDown={(event) => {
              if (event.key === "Escape") closeEmailEditor();
            }}
          >
            <button
              type="button"
              className="profile-dialog__close"
              onClick={closeEmailEditor}
              aria-label="Fechar alteração de e-mail"
            >
              <CloseRoundedIcon aria-hidden="true" />
            </button>
            <h2 id="profile-dialog-title">Alterar e-mail</h2>
            <p>Informe o endereço que deseja usar para receber os retornos.</p>
            <form onSubmit={handleEmailSubmit} noValidate>
              <label htmlFor="profile-email">Novo e-mail</label>
              <input
                id="profile-email"
                type="email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setValidationMessage("");
                }}
                autoComplete="email"
                aria-invalid={Boolean(validationMessage)}
                aria-describedby={validationMessage ? "profile-email-error" : undefined}
              />
              {validationMessage && (
                <span id="profile-email-error" className="profile-dialog__error" role="alert">
                  {validationMessage}
                </span>
              )}
              {!validationMessage && error instanceof Error && (
                <span className="profile-dialog__error" role="alert">
                  {error.message}
                </span>
              )}
              <div className="profile-dialog__actions">
                <button type="button" onClick={closeEmailEditor} disabled={isLoading}>
                  Cancelar
                </button>
                <button type="submit" disabled={isLoading}>
                  {isLoading ? "Salvando…" : "Salvar e-mail"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default ProfilePage;
